import pages from '../content/pages.json';
import site from '../content/site.json';
import {competitionName} from '../data/games';
import {GameData} from '../types';

export interface PageMeta {
    /** without the site name; omitted on the home page */
    title?: string;
    description?: string;
    /** defaults to the current path */
    path?: string;
    /** preview image for social media; defaults to the site image */
    image?: string;
    type?: 'website' | 'article';
    noindex?: boolean;
    /** schema.org structured data for this page */
    jsonLd?: object;
}

export const absoluteUrl = (pathOrUrl: string) => (/^https?:\/\//.test(pathOrUrl) ? pathOrUrl : site.url + pathOrUrl);

export const pageTitle = (title?: string) => (title ? `${title} | ${site.name}` : pages.home.title);

/** Shortens text for meta descriptions at a word boundary. */
export const truncate = (text: string, max = 160) =>
    text.length <= max ? text : `${text.slice(0, max - 1).replace(/\s+\S*$/, '')} …`;

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
    let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }
    element.setAttribute('content', content);
}

function setCanonical(url: string) {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
    }
    link.setAttribute('href', url);
}

function setJsonLd(data?: object) {
    let script = document.getElementById('ld-page');
    if (!data) {
        script?.remove();
        return;
    }
    if (!script) {
        script = document.createElement('script');
        script.id = 'ld-page';
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
}

/**
 * Updates title, description, canonical address, social media preview and structured data.
 * scripts/seo.mjs writes the same tags into the pre-generated HTML files.
 */
export function applyPageMeta(meta: PageMeta & { path: string }) {
    const title = pageTitle(meta.title);
    const description = meta.description || site.description;
    const url = absoluteUrl(meta.path);

    document.title = title;
    setMeta('name', 'description', description);
    setMeta('name', 'robots', meta.noindex ? 'noindex, follow' : 'index, follow');
    setCanonical(url);
    setMeta('property', 'og:type', meta.type ?? 'website');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', absoluteUrl(meta.image ?? site.image));
    setJsonLd(meta.jsonLd);
}

/* ---------- structured data (schema.org) ---------- */

const organization = {
    '@type': 'SportsOrganization',
    name: site.legalName,
    url: `${site.url}/`,
    logo: absoluteUrl(site.logo),
};

const homeVenue = {
    '@type': 'Place',
    name: site.homeVenue.name,
    address: {
        '@type': 'PostalAddress',
        streetAddress: site.homeVenue.streetAddress,
        postalCode: site.homeVenue.postalCode,
        addressLocality: site.homeVenue.addressLocality,
        addressRegion: site.homeVenue.addressRegion,
        addressCountry: site.homeVenue.addressCountry,
    },
};

export function newsArticleLd(article: { title: string; path: string; published?: string; modified?: string; imageUrl?: string }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: article.title.slice(0, 110),
        datePublished: article.published,
        dateModified: article.modified ?? article.published,
        image: [article.imageUrl ?? absoluteUrl(site.image)],
        mainEntityOfPage: absoluteUrl(article.path),
        author: organization,
        publisher: organization,
    };
}

/** "2026-10-03 19:30:00" (Austrian time) → "2026-10-03T19:30:00+02:00", independent of the visitor's time zone. */
export function viennaDateTime(termin: string): string {
    const [date, time = '00:00:00'] = termin.split(' ');
    const [year, month, day] = date.split('-').map(Number);
    const [hours, minutes] = time.split(':').map(Number);
    const zoneName = new Intl.DateTimeFormat('en-US', {timeZone: 'Europe/Vienna', timeZoneName: 'shortOffset'} as Intl.DateTimeFormatOptions)
        .formatToParts(new Date(Date.UTC(year, month - 1, day, hours, minutes)))
        .find(part => part.type === 'timeZoneName')?.value ?? '';
    const match = zoneName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    const offset = match ? `${match[1]}${match[2].padStart(2, '0')}:${match[3] ?? '00'}` : '+01:00';
    return `${date}T${time}${offset}`;
}

/** Upcoming games as SportsEvents, so search engines can show them as events. */
export function sportsEventsLd(games: GameData[]) {
    return {
        '@context': 'https://schema.org',
        '@graph': games.map(game => ({
            '@type': 'SportsEvent',
            name: `${game.heim} – ${game.auswaerts}`,
            description: competitionName(game.bewerb),
            sport: 'Volleyball',
            // 00:00 means the time isn't fixed yet
            startDate: game.termin.endsWith('00:00:00') ? game.termin.slice(0, 10) : viennaDateTime(game.termin),
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            location: game.ort === site.homeVenue.name
                ? homeVenue
                : {'@type': 'Place', name: game.ort || 'wird bekanntgegeben', address: game.ort || 'wird bekanntgegeben'},
            homeTeam: {'@type': 'SportsTeam', name: game.heim},
            awayTeam: {'@type': 'SportsTeam', name: game.auswaerts},
            organizer: organization,
            image: absoluteUrl(site.image),
            url: absoluteUrl(pages.schedule.path),
        })),
    };
}
